import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useLocale, useLocalePath, useT } from "../../i18n";
import { SITE_IMAGERY } from "../../config/siteImagery";
import { useSiteSettings } from "../../lib/siteSettings";
import { ResponsiveImage } from "../ResponsiveImage";
import { Phrases } from "../Phrases";

export const Hero = () => {
  const t = useT();
  const lp = useLocalePath();
  const lang = useLocale();
  const reduce = useReducedMotion();
  const s = useSiteSettings();
  const hp = s.homepage || {};
  const copy = hp.hero?.[lang] || {};
  const image = hp.hero_image ? { ...hp.hero_image, alt: copy.image_alt || SITE_IMAGERY.hero.alt } : SITE_IMAGERY.hero;
  const rise = (d) => ({
    initial: reduce ? false : { opacity: 0, y: 32 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: d },
  });

  return (
    <section data-testid="hero-section" className="relative h-[100svh] min-h-[620px] max-h-[1080px] w-full overflow-hidden bg-ink text-white">
      {s.loaded && (
        <ResponsiveImage
          key={image.variants.large}
          image={image}
          sizes="100vw"
          eager
          testId="hero-image"
          className="hero-image absolute inset-0 h-full w-full object-cover object-[70%_center]"
        />
      )}
      <div className="absolute inset-0 bg-black/35" aria-hidden />
      <div className="hero-scrim absolute inset-0" aria-hidden />
      {s.loaded && (
        <div className="container-x relative z-10 flex h-full flex-col justify-end pb-14 sm:pb-20 lg:pb-24">
          <motion.p {...rise(0.2)} className="eyebrow flex items-center gap-4 text-white/70" data-testid="hero-brand">
            <span className="h-px w-10 bg-white/60" aria-hidden />
            {t("hero.brand")}
          </motion.p>
          <motion.h1 {...rise(0.35)} className="h-hero hero-title font-display mt-6" data-testid="hero-title">
            <Phrases text={copy.title || t("hero.title")} />
          </motion.h1>
          <motion.p {...rise(0.5)} className="hero-sub mt-6 max-w-md text-base text-white/75 sm:text-lg" data-testid="hero-subtitle">
            {copy.subtitle || t("hero.subtitle")}
          </motion.p>
          <motion.div {...rise(0.65)} className="mt-9 flex flex-col gap-2.5 sm:mt-10 sm:flex-row sm:gap-4">
            <Link to={lp("inventory")} className="btn btn-light" data-testid="hero-cta-inventory">
              {t("hero.ctaInventory")}
            </Link>
            <Link to={lp("sourcing")} className="btn btn-ghost-light" data-testid="hero-cta-sourcing">
              {t("hero.ctaSourcing")}
            </Link>
          </motion.div>
        </div>
      )}
      <div className="absolute bottom-24 right-12 z-10 hidden flex-col items-center gap-4 lg:flex" aria-hidden>
        <span className="eyebrow text-white/50 [writing-mode:vertical-rl]">{t("ui.scroll")}</span>
        <span className="scroll-line h-16 w-px bg-white/40" />
      </div>
    </section>
  );
};
